/**
 * HelpBookingFlow — in-app step-by-step booking creation guide.
 *
 * Route: /help/booking-flow. Renders three creation paths (internal
 * admin, approved-booker public link, driver PWA) as vertical
 * step-cards with clear numbered flow. No dependency on a chart lib —
 * pure Tailwind + lucide icons so it stays snappy and works offline.
 *
 * Companion doc: /app/docs/booking-flow.md holds the same content in
 * Markdown + Mermaid for engineers.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Link as LinkIcon,
  Mail,
  Send,
  Smartphone,
  Truck,
  UserPlus,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

function Step({ n, icon: Icon, title, children, testId }) {
  return (
    <div className="flex gap-3" data-testid={testId}>
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">
          {n}
        </div>
        <div className="flex-1 w-px bg-slate-200 mt-1" />
      </div>
      <div className="pb-6 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Icon className="h-4 w-4 text-slate-500" />
          {title}
        </div>
        <div className="text-sm text-slate-600 mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function FlowCard({ title, tagline, testId, children }) {
  return (
    <Card className="h-full" data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <p className="text-xs text-slate-500 mt-1">{tagline}</p>
      </CardHeader>
      <CardContent className="pt-2">{children}</CardContent>
    </Card>
  );
}

export default function HelpBookingFlow() {
  return (
    <div className="space-y-6" data-testid="help-booking-flow-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Help</div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1">How to create a booking</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Three ways bookings enter the system. Pick the path that matches your role. Each path
          ends with the same result: a scheduled slot at a specific bay, tied to a client, with
          an audit trail and email notifications.
        </p>
      </div>

      {/* Overview strip */}
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="outline" className="gap-1"><Users className="h-3 w-3" /> Internal staff</Badge>
            <ArrowRight className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Full form, any site/bay</span>
            <span className="mx-3 text-slate-300">|</span>
            <Badge variant="outline" className="gap-1"><UserPlus className="h-3 w-3" /> Approved booker</Badge>
            <ArrowRight className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Public link, one client</span>
            <span className="mx-3 text-slate-300">|</span>
            <Badge variant="outline" className="gap-1"><Smartphone className="h-3 w-3" /> Driver</Badge>
            <ArrowRight className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Self-service via emailed link</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* PATH 1 — Internal staff */}
        <FlowCard
          title="1. Internal staff"
          tagline="super_admin · client_admin · site_admin · dock_manager"
          testId="flow-internal"
        >
          <Step n={1} icon={Calendar} title="Open Dock Manager" testId="internal-step-1">
            From the top nav, click <strong>Dock manager</strong>. Pick the site whose grid you
            want to book against. The 48-hour grid shows one column per bay, colour-coded.
          </Step>
          <Step n={2} icon={ClipboardCheck} title="Click an empty slot" testId="internal-step-2">
            Click any empty cell on a bay row. This pre-fills the site, bay and time. Alternatively
            click <strong>+ New booking</strong> in the top bar to start from scratch.
          </Step>
          <Step n={3} icon={FileText} title="Fill the form" testId="internal-step-3">
            Fill sender / receiver / vehicle / goods tabs. Required fields flash red if missing.
            Optional: attach a Purchase Order or manifest — the Doc-AI will pre-populate what it
            can extract from the file.
          </Step>
          <Step n={4} icon={CheckCircle2} title="Create → status = approved" testId="internal-step-4">
            Internal staff bookings default to <strong>approved</strong> immediately (no queue).
            A reference number is auto-generated on save.
          </Step>
          <Step n={5} icon={Mail} title="Emails fire" testId="internal-step-5">
            Confirmation goes to the booker&apos;s email + any CC recipients configured for the site
            (see <em>Administration → Email Handling</em>). Driver gets a Driver Info link if their
            phone is set.
          </Step>
        </FlowCard>

        {/* PATH 2 — Approved Booker */}
        <FlowCard
          title="2. Approved booker (external)"
          tagline="Customers, carriers — bookings via emailed public link"
          testId="flow-booker"
        >
          <Step n={1} icon={LinkIcon} title="Admin creates a public link" testId="booker-step-1">
            In <strong>Administration → Public links</strong>, create a link for one site with
            allowed direction(s), max bookings/day and how far ahead they can book. Copy the URL
            (looks like <code className="text-xs">/book/xyz-slug</code>) and email it.
          </Step>
          <Step n={2} icon={Send} title="Booker opens the link" testId="booker-step-2">
            The link works on phone and desktop. No login required — just accept the Terms of
            Service if enabled.
          </Step>
          <Step n={3} icon={FileText} title="Fill sender / receiver / vehicle" testId="booker-step-3">
            The public form is a stripped-down version of the internal form — only fields the
            booker needs to know. Site, bay and client are all locked to the link.
          </Step>
          <Step n={4} icon={CheckCircle2} title="Submit → status = pending" testId="booker-step-4">
            By default, approved-booker submissions land in <strong>pending_approval</strong>.
            If the client_admin ticked <em>Auto-approve bookings</em> for this booker, it skips
            straight to approved.
          </Step>
          <Step n={5} icon={Mail} title="Emails fire" testId="booker-step-5">
            Booker gets a &ldquo;we received your booking&rdquo; email. Site staff get a &ldquo;new booking to
            review&rdquo; email (via the CC recipient list). Approve/reject from{" "}
            <Link to="/bookings" className="text-blue-600 hover:underline">/bookings</Link>{" "}
            with the pending filter.
          </Step>
        </FlowCard>

        {/* PATH 3 — Driver */}
        <FlowCard
          title="3. Driver self-service"
          tagline="Driver picks / updates their own booking on phone"
          testId="flow-driver"
        >
          <Step n={1} icon={Truck} title="Booking is assigned" testId="driver-step-1">
            When an internal booking has a driver phone number, the system emails / SMSes a
            personal Driver Info link (a signed magic link — no password).
          </Step>
          <Step n={2} icon={Smartphone} title="Driver opens the link" testId="driver-step-2">
            The Driver page shows their upcoming bookings. Add to home screen for one-tap access
            (see the Driver PWA once enabled).
          </Step>
          <Step n={3} icon={ClipboardCheck} title="Update details" testId="driver-step-3">
            Driver can add trailer regos, ETA, contact numbers — whatever fields the client admin
            allowed in <em>Administration → Booker/Driver editable fields</em>.
          </Step>
          <Step n={4} icon={Building2} title="Site staff notified" testId="driver-step-4">
            Any material change fires a &ldquo;booking updated&rdquo; email to the site inbox and any CCs.
            The Activity feed on the booking shows exactly what changed and by whom.
          </Step>
        </FlowCard>
      </div>

      {/* After creation */}
      <Card>
        <CardHeader><CardTitle className="text-base">What happens after &ldquo;Create&rdquo;?</CardTitle></CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">Emails</div>
              <ul className="list-disc list-inside space-y-1 text-slate-700">
                <li>Booker receives confirmation.</li>
                <li>CC recipients (configured per site or bay) receive a copy.</li>
                <li>Driver receives a Driver Info magic link (if phone is set).</li>
                <li>All templates editable at <em>Administration → Email templates</em>.</li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">Audit</div>
              <ul className="list-disc list-inside space-y-1 text-slate-700">
                <li>Reference number is auto-generated and unique per client.</li>
                <li>Every edit is recorded in the Activity feed on the booking dialog.</li>
                <li>Printable record: open the booking → click <strong>Print</strong>.</li>
                <li>Bulk export: coming soon (Bookings → CSV export).</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader><CardTitle className="text-base">Common questions</CardTitle></CardHeader>
        <CardContent className="pt-2 text-sm space-y-3 text-slate-800">
          <div>
            <div className="font-semibold">The site inbox didn&apos;t receive a confirmation email.</div>
            <div className="text-slate-700">
              Add the site&apos;s email as a <em>CC recipient</em> at{" "}
              <Link to="/admin/email-handling" className="text-blue-600 hover:underline">Administration → Email Handling</Link>.
              Scope = <em>Client × Site</em>, roles empty (fires on every template).
            </div>
          </div>
          <div>
            <div className="font-semibold">I need to print or export a booking.</div>
            <div className="text-slate-700">
              Open the booking → click the <strong>Print</strong> button in the top-right of the
              dialog. Opens in a new tab, auto-opens your browser&apos;s print dialog. Save as PDF for
              records.
            </div>
          </div>
          <div>
            <div className="font-semibold">Approved-booker submissions land in &ldquo;pending&rdquo;. Can I auto-approve?</div>
            <div className="text-slate-700">
              Go to <em>Administration → Approved bookers</em>, edit the booker, tick{" "}
              <em>Auto-approve bookings</em>.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
